// Finds the person in a picture: MediaPipe's selfie segmentation model, which runs in the browser (WebAssembly, and the graphics card where it can). Its files are in
// public/mediapipe and are loaded the first time they are needed, so that pages that do not use them do not pay for them.

const BASE = `${process.env.PUBLIC_URL || ""}/mediapipe/`;
let loading = null;
let segmenter = null;
let pending = null;
let model = -1;

const loadScript = (src) => new Promise((resolve, reject) => {
  if (window.SelfieSegmentation) { resolve(); return; }
  const tag = document.createElement("script");
  tag.src = src;
  tag.crossOrigin = "anonymous";
  tag.onload = () => resolve();
  tag.onerror = () => reject(new Error("The person-detection files could not be loaded. Check your connection and reload the page."));
  document.head.appendChild(tag);
});

// Loads the model once. `landscape`: a wide picture (the model made for them is faster and just as good), else the general one.
export async function loadSegmenter(landscape = true) {
  if (!loading) {
    loading = (async () => {
      await loadScript(`${BASE}selfie_segmentation.js`);
      const seg = new window.SelfieSegmentation({ locateFile: (file) => `${BASE}${file}` });
      seg.onResults((results) => { const p = pending; pending = null; if (p) p.resolve(results.segmentationMask); });
      await seg.initialize();
      segmenter = seg;
    })().catch((e) => { loading = null; throw e; });
  }
  await loading;
  const wanted = landscape ? 1 : 0;
  if (model !== wanted) { segmenter.setOptions({ modelSelection: wanted, selfieMode: false }); model = wanted; }
  return true;
}

// Resolves with the mask of the person in `source` (a video or image): a picture the size of the input in which the person is bright (the alpha / red channel). It is only valid until the
// next call, so it must be copied (for example to a texture) at once. Pictures are processed one at a time.
export async function segment(source) {
  if (!segmenter) throw new Error("The person-detection model is not loaded.");
  while (pending) await new Promise((r) => setTimeout(r, 2));
  return new Promise((resolve, reject) => {
    pending = { resolve, reject };
    segmenter.send({ image: source }).catch((e) => { pending = null; reject(e); });
  });
}

export const segmenterReady = () => !!segmenter;
